import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import {
  Compass,
  Search,
  Clock,
  AlertCircle,
  Loader2,
  X,
  Globe,
  Tag,
  Sparkles,
  Layers,
} from 'lucide-react';
import {
  skillsCatalogApi,
  type CatalogCategory,
  type CatalogResponse,
  type CatalogSkill,
  type CatalogSkillDetail,
} from '@/services/api';

// ---------------------------------------------------------------------------
// Category color mapping — matches the backend category_color field to
// Tailwind classes. Keeping this in one place so the palette can evolve.
// ---------------------------------------------------------------------------

const CATEGORY_STYLES: Record<
  string,
  { pill: string; dot: string; ring: string; accent: string }
> = {
  teal: {
    pill: 'bg-brand-teal/10 text-brand-teal border-brand-teal/30',
    dot: 'bg-brand-teal',
    ring: 'ring-brand-teal/30',
    accent: 'text-brand-teal',
  },
  purple: {
    pill: 'bg-purple-100 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
    ring: 'ring-purple-300',
    accent: 'text-purple-600',
  },
  amber: {
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    ring: 'ring-amber-300',
    accent: 'text-amber-600',
  },
  emerald: {
    pill: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-300',
    accent: 'text-emerald-600',
  },
  rose: {
    pill: 'bg-rose-100 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    ring: 'ring-rose-300',
    accent: 'text-rose-600',
  },
  slate: {
    pill: 'bg-slate-100 text-slate-700 border-slate-200',
    dot: 'bg-slate-400',
    ring: 'ring-slate-300',
    accent: 'text-slate-600',
  },
};

function styleFor(color: string) {
  return CATEGORY_STYLES[color] ?? CATEGORY_STYLES.slate;
}

// ---------------------------------------------------------------------------
// Skill card
// ---------------------------------------------------------------------------

function SkillCard({
  skill,
  onClick,
}: {
  skill: CatalogSkill;
  onClick: () => void;
}) {
  const s = styleFor(skill.category_color);
  return (
    <button
      onClick={onClick}
      className="group text-left bg-white border border-slate-100 rounded-xl p-4 hover:border-brand-teal/40 hover:shadow-soft transition-all flex flex-col gap-3 h-full"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl leading-none flex-shrink-0">
            {skill.icon ?? '⚙️'}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-navy-900 leading-tight truncate">
              {skill.display_name}
            </h3>
            <p className="text-[10px] font-mono text-slate-400 truncate">
              {skill.skill_id}
            </p>
          </div>
        </div>
        <span
          className={clsx(
            'inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-wide flex-shrink-0',
            s.pill,
          )}
        >
          <span className={clsx('w-1.5 h-1.5 rounded-full', s.dot)} />
          {skill.category_label}
        </span>
      </div>

      {skill.description && (
        <p className="text-xs text-slate-500 line-clamp-3">{skill.description}</p>
      )}

      <div className="flex flex-wrap gap-1 mt-auto">
        {skill.tags.slice(0, 4).map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-0.5 text-[9px] bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded"
          >
            <Tag size={8} />
            {t}
          </span>
        ))}
        {skill.tags.length > 4 && (
          <span className="text-[9px] text-slate-400">
            +{skill.tags.length - 4}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-50 text-[10px]">
        <div className="flex items-center gap-1 text-slate-500">
          <Globe size={10} />
          <span>Global</span>
        </div>
        {skill.schedulable ? (
          <div className="flex items-center gap-1 text-emerald-600 font-medium">
            <Clock size={10} />
            Schedulable
          </div>
        ) : (
          <span className="text-slate-300">on-demand</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — renders the full SKILL.md markdown body as pre-formatted
// text. A proper markdown renderer could be added later; prose styling here
// keeps the diff small and the dependency surface flat.
// ---------------------------------------------------------------------------

function SkillDrawer({
  skillId,
  onClose,
}: {
  skillId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<CatalogSkillDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    skillsCatalogApi
      .getDetail(skillId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Failed to load skill');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  const skill = detail?.skill;
  const style = skill ? styleFor(skill.category_color) : styleFor('slate');

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 bg-slate-50">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{skill?.icon ?? '⚙️'}</span>
              <h2 className="text-lg font-bold text-navy-900 truncate">
                {skill?.display_name ?? skillId}
              </h2>
              {skill && (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-wide',
                    style.pill,
                  )}
                >
                  {skill.category_label}
                </span>
              )}
            </div>
            <p className="text-xs font-mono text-slate-400 truncate">
              {skill?.source_path ?? skillId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Loading skill…
            </div>
          ) : error ? (
            <div className="m-6 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle size={16} />
              {error}
            </div>
          ) : skill && detail ? (
            <div className="p-6 space-y-5">
              {/* Meta strip */}
              <div className="grid grid-cols-2 gap-2">
                <MetaRow
                  label="Scope"
                  value="Global"
                  icon={Globe}
                  accent={style.accent}
                />
                <MetaRow
                  label="Schedulable"
                  value={skill.schedulable ? 'Yes — coworker-ready' : 'On-demand only'}
                  icon={Clock}
                  accent={skill.schedulable ? 'text-emerald-600' : 'text-slate-400'}
                />
                {skill.schedulable && skill.schedule_suggestion && (
                  <div className="col-span-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                    <p className="text-[10px] font-mono uppercase tracking-wide text-emerald-600 mb-0.5">
                      Suggested cadence
                    </p>
                    <p className="text-xs text-emerald-800">
                      {skill.schedule_suggestion}
                    </p>
                  </div>
                )}
              </div>

              {/* Consumes / produces */}
              {(skill.consumes.length > 0 || skill.produces.length > 0) && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                    <p className="text-[10px] font-mono text-slate-400 mb-1 uppercase tracking-wide">
                      Consumes
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {skill.consumes.length ? (
                        skill.consumes.map((c) => (
                          <span
                            key={c}
                            className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-mono"
                          >
                            {c}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                    <p className="text-[10px] font-mono text-slate-400 mb-1 uppercase tracking-wide">
                      Produces
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {skill.produces.length ? (
                        skill.produces.map((p) => (
                          <span
                            key={p}
                            className="text-[10px] bg-brand-teal/10 text-brand-teal px-1.5 py-0.5 rounded font-mono"
                          >
                            {p}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              {skill.tags.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-2">
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {skill.tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                      >
                        <Tag size={9} />
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Full SKILL.md body */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-2">
                  SKILL.md
                </p>
                <pre className="text-[11px] font-mono bg-slate-50 border border-slate-100 rounded-lg p-4 overflow-auto whitespace-pre-wrap text-slate-700 leading-relaxed max-h-[60vh]">
                  {detail.body_markdown}
                </pre>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MetaRow({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  accent: string;
}) {
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-100 px-3 py-2.5">
      <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-0.5">
        {label}
      </p>
      <p className={clsx('text-xs font-medium flex items-center gap-1', accent)}>
        <Icon size={11} />
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SkillsCatalogPage() {
  const [data, setData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [onlySchedulable, setOnlySchedulable] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    skillsCatalogApi
      .list()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Failed to load catalog');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.skills.filter((s) => {
      if (category && s.category !== category) return false;
      if (onlySchedulable && !s.schedulable) return false;
      if (search) {
        const q = search.toLowerCase();
        const hit =
          s.display_name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.skill_id.toLowerCase().includes(q) ||
          s.tags.some((t) => t.toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
  }, [data, category, onlySchedulable, search]);

  const groupedByCategory = useMemo(() => {
    const groups: Record<string, CatalogSkill[]> = {};
    for (const s of filtered) {
      (groups[s.category] ||= []).push(s);
    }
    return groups;
  }, [filtered]);

  const categoriesWithCounts: CatalogCategory[] = data?.categories ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <Compass size={22} className="text-brand-teal" />
            Skills Catalog
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Browse the platform's global skills by category. Skills tagged{' '}
            <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
              <Clock size={11} /> Schedulable
            </span>{' '}
            are purpose-built to run as recurring Claude coworker tasks —
            agency SEO/CRO pulses, rank tracking, funnel reviews.
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-3 text-xs">
            <StatChip icon={Layers} label="Total" value={data.total_unfiltered} />
            <StatChip
              icon={Clock}
              label="Schedulable"
              value={data.schedulable_count}
              accent="text-emerald-600"
            />
            <StatChip
              icon={Sparkles}
              label="Categories"
              value={categoriesWithCounts.filter((c) => c.count > 0).length}
              accent="text-brand-teal"
            />
          </div>
        )}
      </div>

      {/* Category facets */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <CategoryTile
            label="All"
            count={data.total_unfiltered}
            description="Every global skill"
            color="slate"
            active={category === null}
            onClick={() => setCategory(null)}
          />
          {categoriesWithCounts
            .filter((c) => c.count > 0)
            .map((c) => (
              <CategoryTile
                key={c.key}
                label={c.label}
                count={c.count}
                description={c.description}
                color={c.color}
                active={category === c.key}
                onClick={() => setCategory(category === c.key ? null : c.key)}
              />
            ))}
        </div>
      )}

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search skills, tags, descriptions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
          />
        </div>
        <button
          onClick={() => setOnlySchedulable((v) => !v)}
          className={clsx(
            'flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors',
            onlySchedulable
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
          )}
        >
          <Clock size={12} />
          Schedulable only
          {onlySchedulable && <X size={12} />}
        </button>
        {(category || search || onlySchedulable) && (
          <button
            onClick={() => {
              setCategory(null);
              setSearch('');
              setOnlySchedulable(false);
            }}
            className="text-xs text-slate-500 hover:text-navy-900 underline underline-offset-2"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading catalog…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState search={search} category={category} />
      ) : category ? (
        // Single-category view: flat grid
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <SkillCard
              key={s.skill_id}
              skill={s}
              onClick={() => setSelectedSkillId(s.skill_id)}
            />
          ))}
        </div>
      ) : (
        // All-categories view: grouped by category
        <div className="space-y-8">
          {Object.entries(groupedByCategory).map(([catKey, skills]) => {
            const catMeta = categoriesWithCounts.find((c) => c.key === catKey);
            const s = styleFor(catMeta?.color ?? 'slate');
            return (
              <section key={catKey}>
                <header className="flex items-center gap-2 mb-3">
                  <span className={clsx('w-2 h-2 rounded-full', s.dot)} />
                  <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">
                    {catMeta?.label ?? catKey}
                  </h2>
                  <span className="text-xs text-slate-400">
                    {skills.length}
                  </span>
                  {catMeta?.description && (
                    <span className="text-xs text-slate-500 ml-2">
                      — {catMeta.description}
                    </span>
                  )}
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {skills.map((sk) => (
                    <SkillCard
                      key={sk.skill_id}
                      skill={sk}
                      onClick={() => setSelectedSkillId(sk.skill_id)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {selectedSkillId && (
        <SkillDrawer
          skillId={selectedSkillId}
          onClose={() => setSelectedSkillId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatChip({
  icon: Icon,
  label,
  value,
  accent = 'text-navy-900',
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className="bg-white border border-slate-100 rounded-lg px-3 py-2 flex items-center gap-2">
      <Icon size={14} className="text-slate-400" />
      <div>
        <p className="text-[9px] font-mono uppercase tracking-wide text-slate-400 leading-none">
          {label}
        </p>
        <p className={clsx('text-sm font-bold leading-none mt-1', accent)}>
          {value}
        </p>
      </div>
    </div>
  );
}

function CategoryTile({
  label,
  count,
  description,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  description: string;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  const s = styleFor(color);
  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left rounded-xl border p-3 transition-all',
        active
          ? `bg-white border-brand-teal/40 shadow-soft ring-2 ${s.ring}`
          : 'bg-white border-slate-100 hover:border-slate-200',
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={clsx('w-2 h-2 rounded-full', s.dot)} />
        <span
          className={clsx(
            'text-[10px] font-mono uppercase tracking-wide font-bold',
            active ? s.accent : 'text-slate-500',
          )}
        >
          {label}
        </span>
        <span className="ml-auto text-xs font-bold text-navy-900">{count}</span>
      </div>
      <p className="text-[10px] text-slate-500 line-clamp-2 leading-snug">
        {description}
      </p>
    </button>
  );
}

function EmptyState({
  search,
  category,
}: {
  search: string;
  category: string | null;
}) {
  return (
    <div className="text-center py-16 space-y-3">
      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
        <Sparkles size={20} className="text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-600">
        {search || category
          ? 'No skills match the current filters'
          : 'No skills in the catalog'}
      </p>
      {(search || category) && (
        <p className="text-xs text-slate-400">
          Try clearing filters to see every global skill.
        </p>
      )}
    </div>
  );
}

